import SimpleTmpl from '@Services/SimpleTmpl';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { MAP_CONTEXTS } from '@Pages/Hopinto/constants';
import AppStorage from '@Services/AppStorage';
import forEach from 'lodash/forEach';
import toArray from 'lodash/toArray';
import pick from 'lodash/pick';
import isString from 'lodash/isString';
import extend from 'lodash/extend';
import MessageBroker from '@Services/MessageBroker';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { v4 as uuid } from 'uuid';
import { goToAdvancedSearch, goToLanding } from '@Pages/Hopinto/actions';
import { RxSocketRequest } from '@Services/Common/RxSocketRequest';
import BatchSearchRunner from '@Services/BatchSearchRunner';
import { BATCH_FILE } from '@Services/BatchSearchRunner/constants';

const SOCKET_BYPASS_PARAMS = ['options', 'SSOption'];
const GET_BYPASS_PARAMS = ['remoteuser', 'origin'];

const { format, formatOptional } = SimpleTmpl;
const requestUrl = RequestUrls.searchBatchFile;

/**
 * @ngdoc method
 * @name getStructures
 * @param {Object} batchFile data
 * @returns [Array] a list of all structures within batchFile data
 * @description retrieves all structures from batchFile data and puts those into array
 */
function getStructures(batchFile) {
  let structures = [];
  const facts = toArray(batchFile.queries);

  eachHierarchy(
    facts,
    (fact) => {
      if (fact && fact.structures) {
        structures = structures.concat(fact.structures);
      }
    },
    null,
    { children: 'facts' }
  );

  return structures;
}

/**
 * @ngdoc method
 * @name updateOptions
 * @param {Array} structures
 * @param {Array} options
 * @description update options for each structure
 */
function updateOptions(structures, options) {
  forEach(structures, (structure) => {
    // eslint-disable-next-line no-param-reassign
    structure.options = structure.options || options || [BATCH_FILE.structureOptions.DEFAULT_QUERY_TYPE_OPTION];
  });
}

/**
 * @ngdoc method
 * @name proccessBatchFile
 * @param {Object} params $stateParams from hopinto
 * @description get batchFile data from BE, parse it, update options for structures and initiate search by
 *              batchFile's data
 */
// eslint-disable-next-line import/prefer-default-export
export const proccessBatchFile = (params) => {
  const queryId = params.file;
  const url = formatOptional(requestUrl.url, { queryId, ...pick(params, GET_BYPASS_PARAMS) });

  // context is required. If not specified in hopinto - get saved from advanced query context or fallback to 'substances'
  const context = MAP_CONTEXTS[params.context] || AppStorage.instance.get('qbSearchDB') || MAP_CONTEXTS.S;

  return (async () => {
    try {
      const fileName = await MessageBroker.send({ url, method: 'GET' });
      return isString(fileName) && fileName
        ? RxSocketRequest({
            data: pick(params, SOCKET_BYPASS_PARAMS), // bypassed params, currently supports options and SSOption
            in: format(BATCH_FILE.parser.in, { queryId }),
            out: format(BATCH_FILE.parser.out, { queryId }),
            filter: ({ payload }) => payload.queryId === queryId,
          })
            .then(({ payload }) => {
              const { queries } = payload;

              const structures = getStructures(payload);
              const query = {
                context,
                queries,
                searchId: uuid(),
              };

              // extend with options if returned
              extend(query, pick(payload, 'options'));

              // update options for all structures within batchFile data
              // eslint-disable-next-line no-unused-expressions
              structures.length && updateOptions(structures, payload.options);

              // RXFE-7387: make single query editable, so process it through QB as we do it for e.g. Smiles
              if (payload.totalQueriesCount === 1) {
                const singleQuery = {
                  context: query.context,
                  searchId: query.searchId,
                  queryFactItem: query.queries[0],
                };

                goToAdvancedSearch({
                  importedQuery: singleQuery,
                  autoSearch: 'hopInto',
                });
              } else {
                // initiate search By batchFile data
                // last param {boolean} true is a flag for identifying that it is a batch search from hopinto
                BatchSearchRunner.sendRequest(query, payload, true);
              }
            })
            .catch(() => {
              // redirect to quick search if request failed
              goToLanding();
            })
        : goToLanding(); // redirect to quick search if fileName hasn't come
    } catch (e) {
      return Logger.reject(e);
    }
  })();
};
